import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';
import {MapComponent} from './components/map/map.component';
import {MachineListComponent} from './components/machines/machine-list.component';
import {UsersComponent} from './components/users/users.component';
import {UsersFormComponent} from './components/users/users-form/users-form.component';
import {NotFoundComponent} from './components/not-found/not-found.component';
import {MainComponent} from './main.component';
import {
  AuthGuard,
  MANAGE_ACCOUNT,
  MANAGE_ANALYSIS,
  MANAGE_AUDITS,
  MANAGE_FIRMWARE,
  MANAGE_GROUPS,
  MANAGE_MACHINES,
  MANAGE_MODELS,
  MANAGE_ORGANIZATIONS,
  MANAGE_USERS,
  VIEW_ALARMS
} from './common/auth-guard.service';
import {NotAuthorizedComponent} from './components/not-authorized/not-authorized.component';
import {MachinesFormComponent} from './components/machines/machines-form/machines-form.component';
import {PendingChangesGuard} from './common/can-deactivate/pending-changes-guard.service';
import {PermissionFormComponent} from "./components/users/permissions/permission-form/permission-form.component";
import {ProfileFormComponent} from "./components/users/profiles/profile-form/profile-form.component";
import {MachineModelListComponent} from "./components/machine-models/machine-model-list.component";
import {OrganizationsFormComponent} from "./components/organizations/organizations-form/organizations-form.component";
import {MachineModelFormComponent} from "./components/machine-models/machine-models-form/machine-model-form.component";
import {OrganizationsListComponent} from "./components/organizations/organizations-list.component";
import {BeforeNavigationGuardService} from "./common/can-deactivate/before-navigation-guard.service";
import {MachineGroupsListComponent} from "./components/machine-groups/machine-groups-list.component";
import {
  MachineGroupsFormComponent
} from "./components/machine-groups/machine-groups-form/machine-groups-form.component";
import {FirmwareComponent} from "./components/firmware/firmware.component";
import {AuditsListComponent} from "./components/audits/audits-list.component";
import {AccountComponent} from "./components/account/account.component";
import {AnalysisComponent} from "./components/analysis/analysis.component";
import {UserGroupsComponent} from "./components/users/user-groups/user-groups.component";
import {OrganizationTypesListComponent} from "./components/organization-types/organization-types-list.component";
import {MachineDetailsModule} from './components/machine-details/machine-details.module';
import {MaintenanceModule} from './components/maintenance/maintenance.module';
import {
  OrganizationSubscriptionsComponent
} from './components/organizations/organization-subscriptions/organization-subscriptions.component';
import {ImportCsvComponent} from "./components/import-csv/import-csv.component";
import {SubscriptionsListComponent} from "./components/subscriptions/subscriptions-list.component";

const routes: Routes = [
  {path: 'not-found', component: NotFoundComponent},
  {path: 'not-authorized', component: NotAuthorizedComponent},
  {
    path: '', component: MainComponent, children: [
      {
        path: '', pathMatch: 'full', redirectTo: '/machines'
      },
      {
        path: 'map',
        component: MapComponent,
        canDeactivate: [BeforeNavigationGuardService]
      },
      {
        path: 'machines', children: [
          {path: '', component: MachineListComponent},
          {
            path: 'new',
            component: MachinesFormComponent,
            canActivate: [AuthGuard],
            canDeactivate: [PendingChangesGuard],
            data: MANAGE_MACHINES
          },
          {
            path: ':machineUuid/edit',
            component: MachinesFormComponent,
            canActivate: [AuthGuard],
            canDeactivate: [PendingChangesGuard],
            data: MANAGE_MACHINES
          },
          {
            path: ':machineUuid/details',
            loadChildren: () => import('./components/machine-details/machine-details.module').then(() => MachineDetailsModule),
          }
        ]
      },
      {
        path: 'csv',
        component: ImportCsvComponent,
        canActivate: [AuthGuard],
        canDeactivate: [PendingChangesGuard],
        data: MANAGE_MACHINES
      },
      {
        path: 'analysis', component: AnalysisComponent, canActivate: [AuthGuard], data: MANAGE_ANALYSIS
      },
      {
        path: 'maintenance',
        canActivate: [AuthGuard],
        data: VIEW_ALARMS,
        loadChildren: () => import('./components/maintenance/maintenance.module').then(() => MaintenanceModule),
      },
      {
        path: 'organizations', canActivate: [AuthGuard], data: MANAGE_ORGANIZATIONS, children: [
          {path: '', component: OrganizationsListComponent},
          {path: 'new', component: OrganizationsFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: ':uuid/edit', component: OrganizationsFormComponent, canDeactivate: [PendingChangesGuard]},
          {
            path: ':organizationUuid/subscriptions',
            component: OrganizationSubscriptionsComponent,
            canDeactivate: [PendingChangesGuard]
          }
        ]
      },
      {
        path: 'organizationTypes', canActivate: [AuthGuard], data: MANAGE_ORGANIZATIONS, children: [
          {path: '', component: OrganizationTypesListComponent, canDeactivate: [PendingChangesGuard]}
        ]
      },
      {
        path: 'groups', canActivate: [AuthGuard], data: MANAGE_GROUPS, children: [
          {path: '', component: MachineGroupsListComponent},
          {path: 'new', component: MachineGroupsFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: ':uuid/edit', component: MachineGroupsFormComponent, canDeactivate: [PendingChangesGuard]}
        ]
      },
      {
        path: 'users', canActivate: [AuthGuard], data: MANAGE_USERS, children: [
          {path: '', component: UsersComponent},
          {path: 'new', component: UsersFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: ':uuid/edit', component: UsersFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: ':uuid/groups', component: UserGroupsComponent, canDeactivate: [PendingChangesGuard]},
          {path: 'permissions/new', component: PermissionFormComponent, canDeactivate: [PendingChangesGuard]},
          {
            path: 'permissions/:uuid/edit',
            component: PermissionFormComponent,
            canDeactivate: [PendingChangesGuard]
          },
          {path: 'profiles/new', component: ProfileFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: 'profiles/:uuid/edit', component: ProfileFormComponent, canDeactivate: [PendingChangesGuard]}
        ]
      },
      {
        path: 'models', canActivate: [AuthGuard], data: MANAGE_MODELS, children: [
          {path: '', component: MachineModelListComponent},
          {path: 'new', component: MachineModelFormComponent, canDeactivate: [PendingChangesGuard]},
          {path: ':uuid/edit', component: MachineModelFormComponent, canDeactivate: [PendingChangesGuard]}
        ]
      },
      {
        path: 'firmware',
        component: FirmwareComponent,
        canActivate: [AuthGuard],
        data: MANAGE_FIRMWARE
      },
      {
        path: 'audits', component: AuditsListComponent, canActivate: [AuthGuard], data: MANAGE_AUDITS
      },
      {
        path: 'subscriptions', component: SubscriptionsListComponent, canActivate: [AuthGuard], data: MANAGE_MACHINES
      },
      {
        path: 'account',
        component: AccountComponent,
        canActivate: [AuthGuard],
        canDeactivate: [PendingChangesGuard],
        data: MANAGE_ACCOUNT
      }
    ]
  },
  {path: '**', redirectTo: '/not-found'},
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {paramsInheritanceStrategy: 'always'})],
  providers: [AuthGuard],
  exports: [RouterModule]
})
export class AppRoutingModule {
}
